import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import React from 'react';
import type { RegistryDocument } from '../data/types';
import { useRegistry } from '../store/RegistryStore';
import CallNoticePreview from '../manager/calls/CallNoticePreview';
import {
  CapitalReturnAdviceView,
  DistributionAdviceView,
  ExitStatementView,
  HoldingStatementView,
  LedgerLetterView,
} from './AdviceViews';
import AmmaStatementView from './AmmaStatementView';
import InvestorStatementView from './InvestorStatementView';
import UnitCertificateView from './UnitCertificateView';

function DocumentBody({ doc }: { doc: RegistryDocument }) {
  const { state } = useRegistry();
  switch (doc.kind) {
    case 'TAX_STATEMENT':
      return (
        <AmmaStatementView
          state={state}
          holderId={doc.holderId ?? ''}
          financialYear={doc.refId ?? doc.name.match(/FY\d{4}/)?.[0] ?? 'FY2027'}
          fundId={doc.fundId}
          issued={doc.date}
        />
      );
    case 'DISTRIBUTION_ADVICE':
      return <DistributionAdviceView state={state} doc={doc} />;
    case 'CAPITAL_RETURN_ADVICE':
      return <CapitalReturnAdviceView state={state} doc={doc} />;
    case 'EXIT_STATEMENT':
      return <ExitStatementView state={state} doc={doc} />;
    case 'HOLDING_STATEMENT':
      return <HoldingStatementView state={state} doc={doc} />;
    case 'PERIODIC_STATEMENT':
      return <InvestorStatementView state={state} doc={doc} />;
    case 'CALL_NOTICE': {
      const call = state.calls.find((item) => item.id === doc.refId);
      const line = call?.lines.find((item) => item.holderId === doc.holderId);
      const fund = state.funds.find((item) => item.id === call?.fundId);
      const holder = state.holders.find((item) => item.id === doc.holderId);
      if (!call || !line || !fund)
        return <Alert severity="info">Notice details are not available.</Alert>;
      return (
        <CallNoticePreview
          fund={fund}
          holderName={holder?.name ?? ''}
          amount={line.amount}
          callPct={call.callPct}
          dueDate={call.dueDate}
          purpose={call.purpose}
          reference={line.crn}
        />
      );
    }
    case 'UNIT_CERTIFICATE':
      return <UnitCertificateView state={state} doc={doc} />;
    case 'INVESTOR_REPORT':
      return (
        <Alert severity="info">
          The quarterly report PDF that Fides prepares. Uploaded files aren&apos;t stored in the
          prototype.
        </Alert>
      );
    case 'ID_DOCUMENT':
    case 'WHOLESALE_CERTIFICATE':
    case 'APPLICATION_FORM':
      return (
        <Alert severity="info">
          This is an uploaded file. Uploaded files aren&apos;t stored in the prototype.
        </Alert>
      );
    default:
      return <LedgerLetterView state={state} doc={doc} />;
  }
}

export default function DocumentViewerDialog({
  doc,
  onClose,
}: {
  doc: RegistryDocument | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={Boolean(doc)} onClose={onClose} maxWidth="md" fullWidth scroll="body">
      <DialogTitle sx={{ fontWeight: 700, pr: 7 }}>
        {doc?.name}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: '#F1F5F9', pt: '16px !important' }}>
        {doc && (
          <>
            <DocumentBody doc={doc} />
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Box>
                <Button startIcon={<DownloadOutlinedIcon />} onClick={() => window.print()}>
                  Print or save as PDF
                </Button>
              </Box>
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
